import { fetchUserProfileClient } from "@services/userProfileClient";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { getClientLoggedIn } from "@utils/utils";

const LEAD_DATA_KEY = "leadData";

export const getLeadData = async (): Promise<Record<string, any>> => {
  if (typeof window === "undefined") return {};

  try {
    let leadData: Record<string, any> = {};

    // Try sessionStorage first
    const sessionData = sessionStorage?.getItem(LEAD_DATA_KEY);
    if (sessionData) {
      leadData = JSON.parse(sessionData);
    }

    // Fallback to localStorage if sessionStorage has no lead data
    if (!Object.keys(leadData).length) {
      const localData = localStorage?.getItem(LEAD_DATA_KEY);
      if (localData) {
        leadData = JSON.parse(localData);
      }
    }

    const isLoggedIn = getClientLoggedIn();
    if (!isLoggedIn) {
      if (leadData.phone) {
        const { phone: _stalePhone, ...sanitizedLeadData } = leadData;
        setLeadData(sanitizedLeadData);
        return sanitizedLeadData;
      }
      return leadData;
    }

    if (leadData.phone) {
      return leadData;
    }

    const contactNumber = sessionStorage?.getItem("contact_number");
    if (contactNumber) {
      const hydratedLeadData = { ...leadData, phone: contactNumber };
      setLeadData(hydratedLeadData);
      return hydratedLeadData;
    }

    try {
      const profileResponse = await fetchUserProfileClient();
      if (profileResponse.errorStatus) {
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          additionalProps: {
            exception_type: "api_error",
            exception_name: "failed_profile_fetch_for_lead_data",
            vehicle_make: profileResponse.error,
          },
        });

        console.error("[Lead Data Storage] Failed to fetch profile:", profileResponse.error);
      } else {
        const phoneNumber = profileResponse.data?.contact_no;
        if (phoneNumber) {
          const hydratedLeadData = { ...leadData, phone: phoneNumber };
          setLeadData(hydratedLeadData);
          console.log("[Lead Data Storage] ✅ Phone number retrieved from profile API:", phoneNumber);
          return hydratedLeadData;
        }
      }
    } catch (error: any) {
      await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
        additionalProps: {
          exception_type: "api_error",
          exception_name: "failed_profile_fetch_for_lead_data",
          vehicle_make: error?.message || "Unknown error",
        },
      });

      console.error("[Lead Data Storage] Exception fetching profile:", error);
    }

    return leadData;
  } catch (error) {
    console.error("[Lead Data Storage] Error in getLeadData:", error);
    return {};
  }
};

export const setLeadData = (data: Record<string, any>): void => {
  if (typeof window === "undefined") return;

  try {
    const jsonString = JSON.stringify(data);
    sessionStorage.setItem(LEAD_DATA_KEY, jsonString);
    localStorage.setItem(LEAD_DATA_KEY, jsonString);
  } catch (error) {
    console.error("Error saving lead data:", error);
  }
};

export const clearLeadData = (): void => {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(LEAD_DATA_KEY);
    sessionStorage.removeItem("contact_number");
    localStorage.removeItem("mobileNumber");
    localStorage.removeItem(LEAD_DATA_KEY);
  }
};
